import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import {
	useLazyDownloadExportFileQuery,
	useLazyGetExportStatusQuery,
} from "@/pages/Private/redux/export/export.api";
import { ExportFileStatus } from "@/pages/Private/redux/export/export.schema";

import { saveFileFromBlob } from "../utils/downloadFile";

const XLSX_FILE_TYPE = {
	description: "Excel File",
	mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	extension: "xlsx",
};

interface UseExportDownloadLinkParams {
	onError: (message?: string) => void;
}

/**
 * Turns the `?export=<id>` link on an "export is ready" notification into a download
 * (PREC-3953).
 *
 * The param is consumed once and then removed from the URL, so a refresh or a back-navigation
 * does not re-download the file. A still-building or failed export explains itself rather than
 * silently doing nothing — the failure mode this whole ticket exists to remove.
 */
export function useExportDownloadLink({ onError }: UseExportDownloadLinkParams): void {
	const [searchParams, setSearchParams] = useSearchParams();
	const [getStatus] = useLazyGetExportStatusQuery();
	const [downloadExport] = useLazyDownloadExportFileQuery();

	// A ref, not state: React 18 StrictMode mounts effects twice in development, and without this
	// the same export downloads twice.
	const handledRef = useRef<string | null>(null);

	const exportParam = searchParams.get("export");

	useEffect(() => {
		if (!exportParam || handledRef.current === exportParam) {
			return;
		}

		handledRef.current = exportParam;

		const exportId = Number(exportParam);

		const consumeParam = () => {
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);

					next.delete("export");

					return next;
				},
				{ replace: true }
			);
		};

		const run = async () => {
			try {
				const info = await getStatus(exportId).unwrap();

				if (info.status !== ExportFileStatus.READY) {
					onError(exportStatusMessage(info.status, info.failureReason));

					return;
				}

				await saveFileFromBlob(downloadExport(exportId).unwrap(), info.fileName, XLSX_FILE_TYPE);
			} catch (error) {
				console.error(error);
				onError();
			} finally {
				consumeParam();
			}
		};

		void run();
	}, [exportParam, getStatus, downloadExport, setSearchParams, onError]);
}

/** Why a non-ready export could not be downloaded. */
function exportStatusMessage(
	status: ExportFileStatus,
	failureReason?: string | null
): string | undefined {
	if (status === ExportFileStatus.PENDING) {
		return "This export is still being prepared. We'll notify you when it's ready.";
	}

	if (status === ExportFileStatus.EXPIRED) {
		return "This export has expired. Please run it again.";
	}

	return failureReason ?? undefined;
}
